import React, { useEffect, useState } from "react";
import { DrawerForm, DrawerFormRef } from "../../../../components/drawers/DrawerForm";
import Indicator from "../../../../components/base/Indicator";
import Icon from "../../../../components/base/Icon";
import { toShortDateWithoutTime, useHub } from "../../../../core/Global";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import CurrentPlan from "../models/CurrentPlan";
import "./PlanChange.css";
import "./AddonsCard.css";


const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })


// Moving down a plan -- wireframe A6 of the plan document.
//
// Never immediate: it happens at the end of the period already paid for, and the billing date
// stays where it is. Nothing is switched off silently either, so whatever the smaller plan will
// not cover is settled here, before it is scheduled: how many people can be signed in at once,
// phone numbers beyond what it includes, and add-ons the bigger plan was including. Until the date
// a banner on every screen says the change is coming, with a way to cancel it.
//
// The pause is offered here too, before the confirm button, as the document asks.
export default function DowngradeDrawer(props: {
    plan: CurrentPlan,
    planCode: string,
    drawerRef?: React.RefObject<DrawerFormRef>,
    onClose: () => void,
    onScheduled: () => void,
    onPauseInstead: () => void
}) {

    const hub = useHub()

    const [preview, previewSet] = useState<any>()
    const [loading, loadingSet] = useState<boolean>(true)

    const [ackUsers, ackUsersSet] = useState<boolean>(false)
    const [release, releaseSet] = useState<string[]>([])
    const [addonChoice, addonChoiceSet] = useState<{ [type: number]: "keep" | "letgo" }>({})

    const load = () => {
        loadingSet(true)
        hub.Post("/accounts/profile/plan/downgrade/preview", { planCode: props.planCode }, res => {
            loadingSet(false)
            if (res.success) previewSet(res.data)
            else toastError(res.message ?? "Couldn't prepare the downgrade.")
        }, () => loadingSet(false))
    }

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const users = preview?.users
    const numbers = preview?.numbers
    const lost: any[] = preview?.lostAddons ?? []
    const effective = toShortDateWithoutTime(preview?.effectiveAt)
    const period = preview?.billing === "annual" ? "year" : "month"

    const kept = (numbers?.held?.length ?? 0) - release.length
    const extraNumbers = Math.max(0, kept - (numbers?.includedThen ?? 0))
    const numbersOver = (numbers?.held?.length ?? 0) > (numbers?.includedThen ?? 0)

    // Moving to Free asks nothing: add-ons stop on their own and everyone is signed out on the day.
    const toFree = !!preview?.toFree
    const usersShort = !toFree && (users?.short ?? 0) > 0
    const usersSettled = !usersShort || ackUsers
    const addonsSettled = lost.every(l => !!addonChoice[l.addonType])
    const keptAddonsCost = lost.filter(l => addonChoice[l.addonType] === "keep").reduce((s, l) => s + (l.price ?? 0), 0)

    const ready = !!preview && usersSettled && addonsSettled && kept >= 1

    const anythingToResolve = usersShort || numbersOver || lost.length > 0

    const toggleRelease = (id: string) =>
        releaseSet(release.includes(id) ? release.filter(f => f !== id) : [...release, id])

    const schedule = (callback: any) => {
        hub.Post("/accounts/profile/plan/downgrade/schedule", {
            planCode: props.planCode,
            acknowledgeUsers: ackUsers,
            releaseLineIds: release,
            keepAddons: lost.filter(l => addonChoice[l.addonType] === "keep").map(l => l.addonType),
            letGoAddons: lost.filter(l => addonChoice[l.addonType] === "letgo").map(l => l.addonType)
        }, res => {
            callback(res)
            if (res.success) {
                toastSuccess(`Your plan changes to ${preview.planName} on ${effective}.`)
                props.onScheduled()
            }
            else toastError(res.message ?? "Couldn't schedule the downgrade.")
        }, () => callback({ success: false }))
    }

    return (
        <>
            <DrawerForm id="drawer-plan-downgrade"
                ref={props.drawerRef}
                title={preview ? "Change to " + preview.planName : "Downgrade"}
                titleIcon="arrow-down-right"
                subtitle={preview ? `${preview.currentPlanName} → ${preview.planName}` : undefined}
                size="medium"
                buttonText={preview ? "Schedule downgrade for " + effective : "Schedule downgrade"}
                confirmDisabled={!ready}
                onClose={props.onClose}
                onSubmit={(body, callback) => schedule(callback)}>

                {
                    loading || !preview ?
                        <div className="py-20"><Indicator /></div>
                        :
                        <div className="plan-change">

                            {
                                preview.pending &&
                                <div className="plan-change-note is-info mb-6">
                                    <Icon icon="circle-info" type="solid" size={14} />
                                    <div>
                                        A downgrade to {preview.pending.planName} is already scheduled. Scheduling
                                        this one replaces it.
                                    </div>
                                </div>
                            }

                            <div className="plan-change-summary mb-5">
                                <div className="plan-change-row">
                                    <span>Requested on</span>
                                    <b>{toShortDateWithoutTime(preview.requestedAt)}</b>
                                </div>
                                <div className="plan-change-row">
                                    <span>Takes effect</span>
                                    <b>{effective} — your renewal date</b>
                                </div>
                                <div className="plan-change-row">
                                    <span>New price from then</span>
                                    <b>{preview.price > 0 ? `${money.format(preview.price)} / ${period}` : "Free — nothing more is charged"}</b>
                                </div>
                            </div>

                            <div className="plan-change-note is-success mb-8">
                                <Icon icon="circle-check" type="solid" size={14} />
                                <div>
                                    You keep every {preview.currentPlanName} feature and allowance until {effective}.
                                    You have already paid for that time. Nothing is refunded{preview.price > 0 ? ", and your billing date does not move" : ""}.
                                    {
                                        preview.price <= 0 &&
                                        " From then your account carries on as a free one: the add-ons your plan included stop " +
                                        "(you can add any of them back later), messages and calls come out of your wallet, and " +
                                        "everyone is signed out that day so the account carries on with one person signed in at a time."
                                    }
                                </div>
                            </div>

                            <div className="plan-change-section-title">What changes on {effective}</div>
                            <div className="plan-change-summary mb-8">
                                {
                                    (preview.allowances ?? []).map((a: any, k: number) =>
                                        <div className="plan-change-row" key={k}>
                                            <span>{a.label}</span>
                                            <b>
                                                {a.now.toLocaleString()}
                                                <Icon icon="arrow-right" type="solid" size={10} className="mx-2 text-gray-500" />
                                                {a.then.toLocaleString()}
                                            </b>
                                        </div>
                                    )
                                }
                                <div className="plan-change-row">
                                    <span>People signed in at once</span>
                                    <b>
                                        {users?.includedNow}
                                        <Icon icon="arrow-right" type="solid" size={10} className="mx-2 text-gray-500" />
                                        {users?.capacityThen}
                                        {(users?.extraUsers ?? 0) > 0 && <span className="text-gray-500 fw-normal"> (incl. {users.extraUsers} extra)</span>}
                                    </b>
                                </div>
                            </div>

                            {
                                anythingToResolve &&
                                <div className="plan-change-section-title">Before {effective} you need to resolve</div>
                            }

                            {
                                /* People. The plans sell people signed in at the same time, not
                                   accounts, so nobody is deleted: the choice is to pay for the room
                                   or to agree to fewer at once. */
                                usersShort &&
                                <div className={"plan-change-resolve mb-5" + (ackUsers ? " is-settled" : "")}>
                                    <div className="plan-change-resolve-title">
                                        You have {users.teamMembers} team members; {preview.planName} lets {users.capacityThen} be signed in at once.
                                    </div>
                                    {
                                        /* Only Pro sells extra users, and only up to 15 in all, so they
                                           are something to add after the change -- never before it. */
                                        (users.extraUsersAfter ?? 0) > 0 &&
                                        <div className="plan-change-resolve-text">
                                            After the change you can add up to {users.extraUsersAfter} extra user{users.extraUsersAfter === 1 ? "" : "s"} on {preview.planName}, at {money.format(users.extraUserPrice)} each a month.
                                        </div>
                                    }
                                    <div className="d-flex flex-wrap gap-3 mt-4">
                                        <label className="form-check form-check-custom form-check-sm align-items-center">
                                            <input className="form-check-input" type="checkbox" checked={ackUsers}
                                                onChange={e => ackUsersSet(e.target.checked)} />
                                            <span className="form-check-label fw-semibold">
                                                Carry on with {users.capacityThen} signed in at once
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            }

                            {
                                /* Phone numbers beyond what the smaller plan includes: keep them at
                                   the extra-number price, or let some go on the date. */
                                numbersOver &&
                                <div className="plan-change-resolve is-settled mb-5">
                                    <div className="plan-change-resolve-title">
                                        You have {numbers.held.length} phone numbers; {preview.planName} includes {numbers.includedThen}.
                                    </div>
                                    <div className="plan-change-resolve-text">
                                        Keep the ones you need — each one over {numbers.includedThen} is {money.format(numbers.extraNumberPrice)}/month.
                                        Unticked numbers are released on {effective} and cannot be recovered.
                                    </div>
                                    <div className="plan-change-numbers mt-4">
                                        {
                                            numbers.held.map((n: any) =>
                                                <label key={n.id} className="form-check form-check-custom form-check-sm align-items-center">
                                                    <input className="form-check-input" type="checkbox"
                                                        checked={!release.includes(n.id)}
                                                        onChange={() => toggleRelease(n.id)} />
                                                    <span className="form-check-label">
                                                        <b>{n.number}</b>
                                                        {n.name && <span className="text-gray-500"> · {n.name}</span>}
                                                    </span>
                                                </label>
                                            )
                                        }
                                    </div>
                                    <div className="mt-3 fs-7 fw-semibold">
                                        {
                                            kept < 1 ? <span className="text-danger">Keep at least one number.</span>
                                                : extraNumbers > 0
                                                    ? `${extraNumbers} extra × ${money.format(numbers.extraNumberPrice)} = ${money.format(extraNumbers * numbers.extraNumberPrice)}/month`
                                                    : "All within what the plan includes."
                                        }
                                    </div>
                                </div>
                            }

                            {
                                /* Add-ons the bigger plan included. Each one is kept -- charged on the
                                   same invoice as the new plan -- or let go, on purpose. */
                                lost.map((l, k) =>
                                    <div key={k} className={"plan-change-resolve mb-5" + (addonChoice[l.addonType] ? " is-settled" : "")}>
                                        <div className="plan-change-resolve-title">
                                            {l.name} is included in {preview.currentPlanName}.
                                        </div>
                                        <div className="plan-change-resolve-text">
                                            On {preview.planName} it is an add-on at {money.format(l.price)}/month.
                                        </div>
                                        <div className="d-flex flex-wrap gap-5 mt-4">
                                            <label className="form-check form-check-custom form-check-sm align-items-center">
                                                <input className="form-check-input" type="radio" name={"lost-" + l.addonType}
                                                    checked={addonChoice[l.addonType] === "keep"}
                                                    onChange={() => addonChoiceSet({ ...addonChoice, [l.addonType]: "keep" })} />
                                                <span className="form-check-label fw-semibold">Keep it ({money.format(l.price)}/month)</span>
                                            </label>
                                            <label className="form-check form-check-custom form-check-sm align-items-center">
                                                <input className="form-check-input" type="radio" name={"lost-" + l.addonType}
                                                    checked={addonChoice[l.addonType] === "letgo"}
                                                    onChange={() => addonChoiceSet({ ...addonChoice, [l.addonType]: "letgo" })} />
                                                <span className="form-check-label fw-semibold">Let it go</span>
                                            </label>
                                        </div>
                                    </div>
                                )
                            }

                            {
                                keptAddonsCost > 0 &&
                                <div className="fs-7 text-gray-700 mb-6">
                                    From {effective}: {money.format(preview.price + keptAddonsCost)}/{period} with the add-ons you keep.
                                </div>
                            }

                            {/* The retention step: offered before the confirm button, as the
                                document asks. */}
                            <div className="plan-change-pause mt-8">
                                <div>
                                    <div className="fw-bold fs-6">Not sure yet? Pause your account instead</div>
                                    <div className="fs-7 text-gray-700 mt-1">
                                        $29 a month for up to three months. Your data and phone numbers are kept;
                                        everyone can still sign in and look, but nothing can be changed or sent.
                                    </div>
                                </div>
                                <button type="button" className="btn btn-sm btn-light-primary flex-shrink-0"
                                    onClick={e => { e.preventDefault(); props.onPauseInstead(); }}>
                                    <Icon icon="pause" type="solid" size={11} className="me-2" />
                                    Pause my account
                                </button>
                            </div>

                            <div className="fs-8 text-gray-500 mt-6">
                                Until {effective} a banner shows that your plan is changing, with a button to cancel the change.
                            </div>

                        </div>
                }

            </DrawerForm>

        </>
    )
}
